import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trash2, CheckCircle, XCircle, Loader2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ApprovalsPanel = () => {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [notes, setNotes] = useState('');
  const [trackEdits, setTrackEdits] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/approvals`, { withCredentials: true });
      setApprovals(r.data);
    } catch { }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleProcess = async (id, action) => {
    setLoading(true);
    try {
      const approval = approvals.find(a => a.id === id);
      const isPack = approval?.type === 'pack';
      await axios.post(`${API_URL}/api/admin/approvals/${id}/process`, {
        action,
        admin_notes: notes,
        track_edits: !isPack ? trackEdits : {},
        pack_edits: isPack ? trackEdits : {},
      }, { withCredentials: true });
      setExpandedId(null); setNotes(''); setTrackEdits({});
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta aprobacion?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/approvals/${id}`, { withCredentials: true });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Eliminar ${selectedIds.size} aprobacion(es) seleccionada(s)?`)) return;
    setDeleting(true);
    try {
      await axios.post(`${API_URL}/api/admin/approvals/delete-batch`, {
        ids: Array.from(selectedIds)
      }, { withCredentials: true });
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
    setDeleting(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === approvals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(approvals.map(a => a.id)));
    }
  };

  const updateTrackEdit = (trackId, field, value) => {
    setTrackEdits(prev => ({
      ...prev,
      [trackId]: { ...prev[trackId], [field]: value }
    }));
  };

  const statusColors = {
    pending: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', label: 'Pendiente' },
    approved: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', label: 'Aprobado' },
    rejected: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', label: 'Rechazado' },
    edited_approved: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', label: 'Editado y Aprobado' },
  };

  return (
    <div data-testid="approvals-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Cola de Aprobaciones</h2>
          <p className="text-white/40 text-xs mt-1">Revisa y aprueba tracks enviados por asistentes</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <button onClick={handleBatchDelete} disabled={deleting}
              className="px-4 py-2 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-400 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="batch-delete-approvals">
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Eliminar {selectedIds.size} seleccionada(s)
            </button>
          )}
          {approvals.length > 0 && (
            <button onClick={toggleSelectAll}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs text-white/50 font-medium"
              data-testid="select-all-approvals">
              {selectedIds.size === approvals.length ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
            </button>
          )}
          <button onClick={load} className="p-2 hover:bg-white/10 rounded-lg text-white/40">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {approvals.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <CheckCircle className="w-10 h-10 text-green-400/20 mx-auto mb-3" />
          <p className="text-white/30 text-sm">No hay aprobaciones pendientes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {approvals.map(a => {
            const sc = statusColors[a.status] || statusColors.pending;
            const isExpanded = expandedId === a.id;
            const isSelected = selectedIds.has(a.id);
            return (
              <div key={a.id} data-testid={`admin-approval-${a.id}`}
                className={`glass-card overflow-hidden border-l-4 ${sc.border} ${isSelected ? 'ring-1 ring-[#E8720C]/40' : ''}`}>
                {/* Header */}
                <div className="flex items-center gap-3 p-4">
                  <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(a.id)}
                    className="rounded border-white/20 shrink-0" data-testid={`approval-check-${a.id}`} />
                  <div className="flex-1 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : a.id)}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{a.submitted_by_name || 'Asistente'}</span>
                      <span className="text-[10px] text-white/30">{a.submitted_by_email}</span>
                      {a.type === 'pack' && <span className="px-2 py-0.5 bg-[#E8720C]/10 text-[#E8720C] rounded-full text-[9px] font-bold">Paquetes</span>}
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${sc.bg} ${sc.text}`}>{sc.label}</span>
                    </div>
                    <div className="text-[10px] text-white/30 mt-1">
                      {a.type === 'pack'
                        ? `${a.packs_info?.length || 0} paquete(s)`
                        : `${a.tracks_info?.length || 0} track(s)`
                      } | {new Date(a.created_at).toLocaleDateString()} {new Date(a.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button onClick={() => handleDelete(a.id)}
                      className="p-1.5 hover:bg-red-500/10 rounded-lg text-white/20 hover:text-red-400 transition-colors"
                      data-testid={`delete-approval-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => setExpandedId(isExpanded ? null : a.id)}
                      className="p-1.5 hover:bg-white/10 rounded-lg text-white/30">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-white/5 p-4 bg-black/20">
                    {/* Track or Pack list with editable fields */}
                    <div className="space-y-2 mb-4">
                      {(a.type === 'pack' ? a.packs_info : a.tracks_info)?.map((item, i) => (
                        <div key={i} data-testid={`approval-item-${item.track_id || item.pack_id}`}
                          className="p-3 bg-white/[0.02] rounded-lg border border-white/5">
                          <div className="flex justify-between mb-2">
                            <span className="text-sm text-white/80 font-medium">
                              {item.title || item.name}
                              {a.type === 'pack' && <span className="text-white/30 text-xs ml-2">({item.track_count} tracks)</span>}
                            </span>
                            {(!a.type || a.type !== 'pack') && <span className="text-xs text-white/40">{item.artist || '-'}</span>}
                          </div>
                          {a.status === 'pending' && (
                            <div className="grid grid-cols-3 gap-2 mt-2">
                              <div>
                                <label className="text-[9px] text-white/30 block mb-1">{a.type === 'pack' ? 'Nombre' : 'Titulo'}</label>
                                <input className="w-full px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#E8720C]/50"
                                  defaultValue={item.title || item.name}
                                  onChange={e => updateTrackEdit(item.track_id || item.pack_id, a.type === 'pack' ? 'name' : 'title', e.target.value)} />
                              </div>
                              {(!a.type || a.type !== 'pack') ? (
                                <div>
                                  <label className="text-[9px] text-white/30 block mb-1">Tag</label>
                                  <input className="w-full px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#E8720C]/50"
                                    defaultValue={item.artist || ''}
                                    onChange={e => updateTrackEdit(item.track_id, 'artist', e.target.value)} />
                                </div>
                              ) : (
                                <div>
                                  <label className="text-[9px] text-white/30 block mb-1">Genero</label>
                                  <input className="w-full px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#E8720C]/50"
                                    defaultValue={item.genre || ''}
                                    onChange={e => updateTrackEdit(item.pack_id, 'genre', e.target.value)} />
                                </div>
                              )}
                              <div>
                                <label className="text-[9px] text-white/30 block mb-1">Precio</label>
                                <input className="w-full px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#E8720C]/50"
                                  type="number" step="0.01" defaultValue={item.price}
                                  onChange={e => updateTrackEdit(item.track_id || item.pack_id, 'price', e.target.value)} />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Admin notes + actions */}
                    {a.status === 'pending' ? (
                      <>
                        <div className="mb-4">
                          <label className="text-[10px] text-white/40 block mb-1">Notas del Admin</label>
                          <textarea data-testid="approval-notes"
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white resize-none h-16 focus:outline-none focus:border-[#E8720C]/50"
                            value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observaciones o razones..." />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button data-testid="reject-btn" onClick={() => handleProcess(a.id, 'reject')} disabled={loading}
                            className="px-4 py-2 bg-red-500/15 hover:bg-red-500/25 text-red-400 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                            <XCircle className="w-3.5 h-3.5" /> Rechazar
                          </button>
                          <button data-testid="edit-approve-btn" onClick={() => handleProcess(a.id, 'edit_approve')} disabled={loading}
                            className="px-4 py-2 bg-orange-500/15 hover:bg-orange-500/25 text-orange-400 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                            <CheckCircle className="w-3.5 h-3.5" /> Editar y Aprobar
                          </button>
                          <button data-testid="approve-btn" onClick={() => handleProcess(a.id, 'approve')} disabled={loading}
                            className="px-4 py-2 bg-green-500/15 hover:bg-green-500/25 text-green-400 rounded-lg text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
                            <CheckCircle className="w-3.5 h-3.5" /> Aprobar
                          </button>
                        </div>
                      </>
                    ) : (
                      a.admin_notes && (
                        <div className="p-3 bg-white/[0.02] rounded-lg border-l-2 border-[#E8720C]">
                          <div className="text-[#E8720C] text-[10px] font-semibold mb-1">Notas del Admin:</div>
                          <div className="text-white/60 text-xs">{a.admin_notes}</div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApprovalsPanel;
